import parsePhoneNumber from 'libphonenumber-js';
import { useEffect, useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { z } from 'zod';

import { faAngleRight } from '@fortawesome/pro-light-svg-icons';
import { faEnvelope, faLock, faPeopleGroup, faPhone } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Box,
    Button,
    Divider,
    IconButton,
    Link as MuiLink,
    Stack,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Switch } from '@sage/ui-components';
import {
    Link,
    createFileRoute,
    useNavigate,
    useParams,
    useRouter,
    useSearch,
} from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';

import { SUPPORT_EMAIL, SUPPORT_PHONE_NUMBER } from '../../../../../constants/SupportInfo';
import LicensedCaregiverDisplay from 'src/components/LicensedCaregiverDisplay';
import LogoutButton from 'src/components/LogoutButton';
import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import StyledIconButton from 'src/components/StyledIconButton';
import TeamMemberAvatar from 'src/components/TeamMemberAvatar';
import ConfirmSmsOptOutModal from 'src/components/modals/ConfirmOptOutModal';
import ConfirmationModal from 'src/components/modals/ConfirmationModal';
import { useModalManager } from 'src/components/modals/ModalManager';
import SelectTeamModal from 'src/components/modals/SelectTeamModal';
import ShareInviteModal from 'src/components/modals/ShareInviteModal';
import { TeamMemberStatuses, TeamRoles } from 'src/constants/TeamRoles';
import { useAnalytics } from 'src/contexts/AnalyticsContext';
import { useSnackbar } from 'src/contexts/SnackbarContext';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import { useDeleteTeamMemberMutation, useUpdateTeamMemberMutation } from 'src/store/sageHomeApi';
import { selectLargeType, setLargeType } from 'src/store/slices/uiSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';

const teamMemberDetailSearchSchema = z.object({
    fromInvite: z.boolean().optional().default(false),
});

export const Route = createFileRoute('/(app)/care/team/member/$teamMemberRid')({
    component: TeamMemberDetailComponent,
    validateSearch: zodValidator(teamMemberDetailSearchSchema),
});

const PageName = 'Team Member Detail';

function TeamMemberDetailComponent() {
    const navigate = useNavigate();
    const { history } = useRouter();
    const { teamMemberRid } = useParams({ from: '/(app)/care/team/member/$teamMemberRid' });
    const { fromInvite } = useSearch({ from: '/(app)/care/team/member/$teamMemberRid' });
    const { spacing, palette, typography } = useTheme();

    const { openModal } = useModalManager();
    const { trackPageView, trackEvent } = useAnalytics();
    const { showSuccess, showError } = useSnackbar();

    const dispatch = useAppDispatch();
    const largeType = useAppSelector(selectLargeType);
    const [storedLargeType, setStoredLargeType] = useLocalStorage('largeType', false);

    const { getTeamMemberDetails, currentUser, teams } = useTeamMemberData();

    const [deleteTeamMember, { isLoading: isDeleting }] = useDeleteTeamMemberMutation();
    const [updateTeamMember, { isLoading: isUpdatingNotifications }] =
        useUpdateTeamMemberMutation();

    const teamMember = useMemo(
        () => getTeamMemberDetails(teamMemberRid),
        [getTeamMemberDetails, teamMemberRid]
    );

    const currentIsTeamAdmin = currentUser?.isTeamAdmin;
    const isCurrentUser = currentUser?.teamMemberRid === teamMemberRid;

    const allNotificationsDisabled =
        !currentUser?.notificationSettings?.smsNotificationsEnabled &&
        !currentUser?.notificationSettings?.emailNotificationsEnabled;

    useEffect(() => {
        trackPageView(PageName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync localStorage with Redux state for large type
    useEffect(() => {
        if (storedLargeType !== largeType) {
            dispatch(setLargeType(storedLargeType));
        }
    }, [storedLargeType, largeType, dispatch]);

    const handleLargeTypeChange = (checked: boolean) => {
        setStoredLargeType(checked);
        dispatch(setLargeType(checked));
        trackEvent('Large Type Toggle Changed', { enabled: checked, context: PageName });
    };

    const handleCancelInvite = async () => {
        if (!teamMember?.teamMemberRid) return;

        try {
            await deleteTeamMember({ teamMemberRid: teamMember.teamMemberRid }).unwrap();
            showSuccess('Invite canceled');
            navigate({ to: '/care/team' });
        } catch (error) {
            console.error('Failed to cancel invite:', error);
            showError('An error occurred, please try again.');
        }
    };

    const handleConfirmCancelInvite = () => {
        openModal({
            content: ConfirmationModal,
            props: {
                title: 'Are you sure you want to cancel this invite?',
                confirmText: 'Cancel Invite',
                cancelText: 'Go Back',
                onConfirm: handleCancelInvite,
            },
        });
    };

    const handleShareInvite = () => {
        if (!teamMember) return;

        openModal({
            content: ShareInviteModal,
            props: { teamMember },
        });
    };

    const handleUpdateNotifications = async (
        smsEnabled: boolean,
        emailEnabled: boolean,
        notificationType?: 'SMS' | 'Email'
    ) => {
        if (!teamMember?.teamMemberRid) return;

        try {
            await updateTeamMember({
                teamMemberRid: teamMember.teamMemberRid,
                updateTeamMemberRequest: {
                    smsNotificationsEnabled: smsEnabled,
                    emailNotificationsEnabled: emailEnabled,
                },
            }).unwrap();
            const successMessage = notificationType
                ? `${notificationType} notifications updated`
                : 'Notification settings updated';
            showSuccess(successMessage);
        } catch (error) {
            console.error('Failed to update notifications:', error);
            showError('An error occurred, please try again.');
        }
    };

    const handleSmsNotificationChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        checked: boolean
    ) => {
        if (checked) {
            // Turning ON - no confirmation needed
            handleUpdateNotifications(
                true,
                notificationSettings?.emailNotificationsEnabled ?? false,
                'SMS'
            );
        } else {
            // Turning OFF - show confirmation
            openModal({
                content: ConfirmSmsOptOutModal,
                props: {
                    notificationType: 'SMS',
                    onConfirm: () =>
                        handleUpdateNotifications(
                            false,
                            notificationSettings?.emailNotificationsEnabled ?? false,
                            'SMS'
                        ),
                },
            });
        }
    };

    const handleEmailNotificationChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        checked: boolean
    ) => {
        if (checked) {
            // Turning ON - no confirmation needed
            handleUpdateNotifications(
                notificationSettings?.smsNotificationsEnabled ?? false,
                true,
                'Email'
            );
        } else {
            // Turning OFF - show confirmation
            openModal({
                content: ConfirmSmsOptOutModal,
                props: {
                    notificationType: 'Email',
                    onConfirm: () =>
                        handleUpdateNotifications(
                            notificationSettings?.smsNotificationsEnabled ?? false,
                            false,
                            'Email'
                        ),
                },
            });
        }
    };

    if (!teamMember) {
        return (
            <InnerMaxWidthContainer>
                <PageHeader pageTitle="Team Member" />
                <Typography variant="body1">Team member not found.</Typography>
            </InnerMaxWidthContainer>
        );
    }

    const {
        firstName,
        lastName,
        email,
        phoneNumber,
        isTeamAdmin: viewedIsTeamAdmin,
        notificationSettings,
    } = teamMember;
    const viewsIsInvited = teamMember?.teamMemberStatus === TeamMemberStatuses.Invited;

    return (
        <InnerMaxWidthContainer>
            <PageHeader
                pageTitle={isCurrentUser ? 'Account' : 'Team Member'}
                actionButton={
                    teams.length > 1 && (
                        <IconButton
                            onClick={() => {
                                trackEvent('Select Team Button Clicked', {
                                    context: PageName,
                                });
                                openModal({ content: SelectTeamModal });
                            }}
                            disableRipple
                        >
                            <FontAwesomeIcon icon={faPeopleGroup} color={palette.primary.main} />
                        </IconButton>
                    )
                }
                onBack={() => {
                    if (fromInvite) {
                        navigate({ to: '/home' });
                    } else {
                        history.back();
                    }
                }}
            />

            <Stack sx={{ gap: 4 }}>
                {/* Team Member Avatar and Name */}
                <Stack sx={{ gap: 2, alignItems: 'center' }}>
                    <TeamMemberAvatar teamMember={teamMember} size="large" />
                    <Stack sx={{ gap: 0.5, alignItems: 'center' }}>
                        <Typography variant="h2" component="h2" sx={{ fontWeight: 600 }}>
                            {firstName} {lastName}
                        </Typography>
                        {viewedIsTeamAdmin && (
                            <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
                                <FontAwesomeIcon icon={faLock} size="sm" />
                                <Typography variant="body2">Team Admin</Typography>
                            </Stack>
                        )}
                        {teamMember.role === TeamRoles.LicensedCaregiver && (
                            <LicensedCaregiverDisplay />
                        )}
                    </Stack>
                </Stack>

                {/* Profile Info title and edit button */}
                <Stack sx={{ gap: 1 }}>
                    <Stack
                        direction="row"
                        sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                    >
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            Profile Info
                        </Typography>
                        {(isCurrentUser || currentIsTeamAdmin) && (
                            <Link
                                to="/care/team/member/edit/$teamMemberRid"
                                params={{ teamMemberRid: teamMember.teamMemberRid }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 600, color: 'primary.main' }}
                                >
                                    Edit
                                </Typography>
                            </Link>
                        )}
                    </Stack>

                    {/* Phone Number and Email */}
                    <Stack
                        sx={{
                            border: '1px solid',
                            borderColor: 'gray.300',
                            borderRadius: spacing(1.5),
                            backgroundColor: 'common.white',
                        }}
                        divider={<Divider />}
                    >
                        <Stack
                            direction="row"
                            sx={{
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                p: 2.5,
                                gap: 2,
                            }}
                        >
                            <Box>
                                <Typography variant="body2">Phone Number</Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, wordBreak: 'break-word' }}
                                >
                                    {parsePhoneNumber(phoneNumber || '')?.formatNational()}
                                </Typography>
                            </Box>
                            <StyledIconButton
                                variant="dark"
                                icon={faPhone}
                                onClick={() => {
                                    trackEvent('Phone Number Button Clicked', {
                                        context: PageName,
                                    });
                                    window.location.href = `tel:${phoneNumber}`;
                                }}
                            />
                        </Stack>
                        <Stack
                            direction="row"
                            sx={{
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                p: 2.5,
                                gap: 2,
                            }}
                        >
                            <Box>
                                <Typography variant="body2">Email</Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, wordBreak: 'break-word' }}
                                >
                                    {email}
                                </Typography>
                            </Box>
                            <StyledIconButton
                                variant="dark"
                                icon={faEnvelope}
                                onClick={() => {
                                    trackEvent('Email Button Clicked', {
                                        context: PageName,
                                    });
                                    window.location.href = `mailto:${email}`;
                                }}
                            />
                        </Stack>
                    </Stack>
                </Stack>

                {/* Notification Settings - only show for current user */}
                {isCurrentUser && (
                    <>
                        <Stack sx={{ gap: 1.5 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                Notification Settings
                            </Typography>

                            <Stack
                                sx={{
                                    border: allNotificationsDisabled ? '2px solid' : '1px solid',
                                    borderColor: allNotificationsDisabled
                                        ? 'error.main'
                                        : 'gray.300',
                                    borderRadius: spacing(1.5),
                                    backgroundColor: 'common.white',
                                }}
                                divider={<Divider />}
                            >
                                {allNotificationsDisabled && (
                                    <Stack sx={{ p: 2.5, gap: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            You're not receiving notifications.
                                        </Typography>
                                        <Typography variant="body2">
                                            Turn them on to stay updated about care activities.
                                        </Typography>
                                    </Stack>
                                )}
                                {/* SMS */}
                                <Stack
                                    direction="row"
                                    sx={{
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 2.5,
                                        gap: 2,
                                    }}
                                >
                                    <Stack>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            SMS
                                        </Typography>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{ textWrap: 'balance' }}
                                        >
                                            Care Summaries and Check-Ins
                                        </Typography>
                                    </Stack>
                                    <Switch
                                        checked={
                                            notificationSettings?.smsNotificationsEnabled ?? false
                                        }
                                        onChange={handleSmsNotificationChange}
                                        disabled={isUpdatingNotifications}
                                        sx={{ m: 0 }}
                                    />
                                </Stack>

                                {/* Email */}
                                <Stack
                                    direction="row"
                                    sx={{
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 2.5,
                                        gap: 2,
                                    }}
                                >
                                    <Stack>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            Email
                                        </Typography>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{ textWrap: 'balance' }}
                                        >
                                            Care Summaries and Check-Ins
                                        </Typography>
                                    </Stack>
                                    <Switch
                                        checked={
                                            notificationSettings?.emailNotificationsEnabled ?? false
                                        }
                                        onChange={handleEmailNotificationChange}
                                        disabled={isUpdatingNotifications}
                                        sx={{ m: 0 }}
                                    />
                                </Stack>
                            </Stack>
                        </Stack>

                        {/* Notification Settings */}
                        <Stack sx={{ gap: 1.5 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                Accessibility Settings
                            </Typography>

                            <Stack
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'gray.300',
                                    borderRadius: spacing(1.5),
                                    backgroundColor: 'common.white',
                                }}
                                divider={<Divider />}
                            >
                                {/* Large Type */}
                                <Stack
                                    direction="row"
                                    sx={{
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 2.5,
                                        gap: 2,
                                    }}
                                >
                                    <Stack>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            Large Type
                                        </Typography>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{ textWrap: 'balance' }}
                                        >
                                            Increase font size for better readability
                                        </Typography>
                                    </Stack>
                                    <Switch
                                        checked={largeType}
                                        onChange={(e, checked) => handleLargeTypeChange(checked)}
                                        sx={{ m: 0 }}
                                    />
                                </Stack>
                            </Stack>
                        </Stack>
                    </>
                )}

                {/* Pending Invite Status */}
                {viewsIsInvited && (
                    <Stack sx={{ alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Pending Invite
                        </Typography>
                        <Typography variant="subtitle2">
                            {`Waiting for ${firstName} to login.`}
                        </Typography>
                    </Stack>
                )}

                {/* Terms and Conditions - only show for current user */}
                {isCurrentUser && (
                    <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        onClick={() => {
                            trackEvent('Terms and Conditions Button Clicked', {
                                context: PageName,
                            });
                            navigate({ to: '/termsAndConditions' });
                        }}
                        endIcon={<FontAwesomeIcon icon={faAngleRight} />}
                        sx={{
                            justifyContent: 'space-between',
                            borderColor: 'gray.300',
                            color: 'gray.900',
                            backgroundColor: 'common.white',
                            fontSize: typography.body2.fontSize,
                        }}
                    >
                        Terms and Conditions
                    </Button>
                )}

                {/* Need Help? */}
                <Stack
                    sx={{
                        border: '1px solid',
                        borderColor: 'gray.300',
                        borderRadius: spacing(1.5),
                        backgroundColor: 'common.white',
                    }}
                >
                    <Stack sx={{ p: 2.5, gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Need Help?
                        </Typography>

                        <Typography variant="body2">
                            Call Sage Home support at{' '}
                            <MuiLink href={`tel:${SUPPORT_PHONE_NUMBER}`}>
                                {parsePhoneNumber(SUPPORT_PHONE_NUMBER)?.formatNational()}
                            </MuiLink>
                        </Typography>

                        <Typography variant="body2">
                            or email us at{' '}
                            <MuiLink href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</MuiLink>
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>

            {/* Logout button - only show for current user */}
            {isCurrentUser && (
                <PageFooter>
                    <LogoutButton />
                </PageFooter>
            )}

            {/* Invite Actions - only show for team admins viewing invited members */}
            {currentIsTeamAdmin && viewsIsInvited && (
                <PageFooter>
                    <Button
                        variant="contained"
                        color="primaryInverse"
                        fullWidth
                        onClick={handleShareInvite}
                    >
                        Share Invite
                    </Button>
                    <Button
                        variant="contained"
                        color="errorInverse"
                        fullWidth
                        onClick={handleConfirmCancelInvite}
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'Canceling...' : 'Cancel Invite'}
                    </Button>
                </PageFooter>
            )}
        </InnerMaxWidthContainer>
    );
}
